import { ApolloQueryResult } from "@apollo/client";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getFormSnakeCaseError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useClientVatTypesApi from "../hooks/useClientVatTypesApi";

interface ClientVatTypeFormValues extends Record<string, unknown> {
  name: string;
  code: string;
}

interface CreateUpdateClientVatTypesPropsTypes {
  id?: ID;
  data?: GetClientInfoResponseData | undefined;
  refetch: (
    variables?: Partial<ListClientVatTypesVars> | undefined,
  ) => Promise<ApolloQueryResult<ListClientVatTypesResponse>>;
}

export default function CreateUpdateClientVatTypes(
  props: CreateUpdateClientVatTypesPropsTypes,
): React.ReactElement {
  const { id, refetch } = props;

  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const { createUpdateClientVatType, getClientVatType } = useClientVatTypesApi(id, form);
  const { notificationSuccessfullySaved } = useNotification();

  useEffect(() => {
    if (id && isModalVisible) {
      getClientVatType({
        variables: {
          id: id,
        },
      });
    }
  }, [isModalVisible]);

  const onFinish = async (values: ClientVatTypeFormValues) => {
    const updateId = id ? { id } : {};
    const variables = {
      input: {
        companyId: urlParams.companyId,
        name: values.name,
        code: values.code,
        ...updateId,
      },
    };

    const response = await createUpdateClientVatType({ variables });
    const { createClientVatType, updateClientVatType } = response?.data || {};
    const validationErrors = createClientVatType?.errors || updateClientVatType?.errors;

    const createErrors = response?.data?.createClientVatType?.errors;
    const updateErrors = response?.data?.updateClientVatType?.errors;

    if (validationErrors && validationErrors.length > 0) {
      const fieldsData = getFormSnakeCaseError(createErrors || updateErrors, values);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Vat type"));
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}
      <Modal
        title={id ? t("Update vat type") : t("Add vat type")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Save")}
        width={768}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        className="antd-modal"
      >
        <Form
          name="createUpdateClientVatType"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24}>
              <Form.Item
                name="name"
                label={t("Name")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="code"
                label={t("Code")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
